import { AssetRecordType, TLShapeId, Editor } from "@tldraw/tldraw";
import { DocumentOrientation } from "pages/flyer/builder/drawer/types";
import { mmToPx } from "pages/flyer/builder/utils";
import { getStoredOrientation } from "pages/flyer/builder/utils/orientationStorage";
import { useMemo } from "react";

const useQRCodePosition = ({
  editor,
  format,
  currentFormatConfig,
  designId,
  campaignId,
  currentOrientation,
}: {
  editor: Editor;
  format: string;
  currentFormatConfig: { displayWidth: number; displayHeight: number };
  designId?: string;
  campaignId?: string;
  currentOrientation?: string;
}) => {
  const storedOrientation =
    currentOrientation || getStoredOrientation(designId, campaignId, DocumentOrientation.Portrait);
  const isPortrait = storedOrientation === DocumentOrientation.Portrait;
  const frontShape = editor.getShape("shape:front" as TLShapeId);

  const qrCode = AssetRecordType.createId("qrCode");
  const bleedsZoneMM = 5;

  return useMemo(() => {
    let x = 0;
    let y = 0;
    let w = 120;
    let h = 120;
    if (!currentFormatConfig) return { x, y, w, h, assetId: qrCode, isPortrait, frontShape, wh: 120 };
    const wh = isPortrait ? currentFormatConfig.displayWidth / 5 : currentFormatConfig.displayWidth / 4;

    switch (format) {
      case "DL":
        x = isPortrait
          ? (frontShape?.x ?? 0) + currentFormatConfig.displayWidth / 2 - wh / 2
          : (frontShape?.x ?? 0) + currentFormatConfig.displayHeight - wh * 3.7;

        y = isPortrait
          ? currentFormatConfig.displayHeight - wh * 2.5
          : (frontShape?.y ?? 0) + currentFormatConfig.displayWidth / 2 - wh / 2 - mmToPx(bleedsZoneMM);

        w = isPortrait ? wh : 120;
        h = isPortrait ? wh : 120;
        break;

      case "A4":
        x = isPortrait
          ? (frontShape?.x ?? 0) + currentFormatConfig.displayWidth / 2 - wh / 2 + 20
          : (frontShape?.x ?? 0) + currentFormatConfig.displayHeight - wh - 230;

        y = isPortrait
          ? currentFormatConfig.displayHeight - wh * 3 + 20
          : (frontShape?.y ?? 0) + currentFormatConfig.displayWidth / 3 - wh / 2 - mmToPx(bleedsZoneMM) + 150;

        w = isPortrait ? wh : 130;
        h = isPortrait ? wh : 130;
        break;

      case "A5":
        x = isPortrait
          ? (frontShape?.x ?? 0) + currentFormatConfig.displayWidth / 2 - wh / 2
          : (frontShape?.x ?? 0) + currentFormatConfig.displayHeight - wh - 150;

        y = isPortrait
          ? currentFormatConfig.displayHeight - wh * 3 + 70
          : (frontShape?.y ?? 0) + currentFormatConfig.displayWidth / 3 - wh / 2 - mmToPx(bleedsZoneMM) + 80;

        w = isPortrait ? wh : 130;
        h = isPortrait ? wh : 130;
        break;

      case "A6":
        x = isPortrait
          ? (frontShape?.x ?? 0) + currentFormatConfig.displayWidth / 2 - wh / 2
          : (frontShape?.x ?? 0) + currentFormatConfig.displayHeight - wh - 160;

        y = isPortrait
          ? currentFormatConfig.displayHeight - wh * 3 + 105
          : (frontShape?.y ?? 0) + currentFormatConfig.displayWidth / 3 - wh / 2 - mmToPx(bleedsZoneMM) + 90;

        w = isPortrait ? wh : 130;
        h = isPortrait ? wh : 130;
        break;

      case "DOOR_HANGER":
        x = (frontShape?.x ?? 0) + currentFormatConfig.displayWidth / 2 - wh / 2;
        y = currentFormatConfig.displayHeight - wh * 3;

        w = wh;
        h = wh;
        break;

      case "TEARAWAY":
        x = (frontShape?.x ?? 0) + currentFormatConfig.displayWidth / 2 - wh / 2;
        y = currentFormatConfig.displayHeight - wh * 3 + 30;

        w = wh;
        h = wh;
        break;

      // removed unused legacy postcard formats

      default:
        x = (frontShape?.x ?? 0) + currentFormatConfig.displayWidth / 2 - wh / 2;
        y = (frontShape?.y ?? 0) + currentFormatConfig.displayHeight / 2 - wh / 2;
        w = wh;
        h = wh;
        break;
    }

    return { x, y, w, h, assetId: qrCode, isPortrait, frontShape, wh };
  }, [format, isPortrait, frontShape, currentFormatConfig, qrCode, currentOrientation]);
};

export default useQRCodePosition;
